/* istanbul ignore file */
import { takeLeading, put, select } from 'redux-saga/effects';
import { callApi } from 'helpers/saga-helpers';
import {
  REQUEST_REJECT_CLAIM,
  REQUEST_CLAIM_RECAP,
  REQUEST_OPEN_CLAIM,
  REQUEST_OPEN_MIXED_CLAIM,
  REQUEST_ASSIGNMENT,
  REQUEST_CORPOREAL_LAWYERS,
  REQUEST_UPDATE_VICTIM_INFO,
  REQUEST_UPDATE_BENEFICIARY_INFO,
  REQUEST_UPDATE_VEHICLE_INFO,
  REQUEST_VALIDATE_VICTIM,
  REQUEST_VALIDATE_VEHICLE,
  REQUEST_VALIDATE_PROVISION_INTERVENANT,
  REQUEST_ADD_CLAIM_NUMBER,
  REQUEST_GET_FEE_NOTES_BY_CLAIM_ID,
  REQUEST_GET_SAGIS_CLAIM,
  REQUEST_VALIDATE_COMPLETION_CLAIM_MANAGER,
} from 'containers/RecapManagerPage/constants';
import { setFeeNotesSearch } from 'containers/LawyerFeeNote/actions';
import {
  setClaimRecap,
  setCorporealLawyers,
  requestClaimRecap,
  requestGetFeeNotesByClaimId,
  setSagisClaim,
} from 'containers/RecapManagerPage/actions';
import userToken from 'containers/services/security/access-token-storage';
import { get, isNull } from 'lodash';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { AssignAction } from 'containers/RecapManagerPage/typings';
import { requestSearchClaims } from 'containers/IdentificationPage/actions';
import { makeSelecOpenedClaim } from 'containers/RecapManagerPage/selectors';
import { formattedAssignementClaim } from 'containers/RecapManagerPage/utils';
import { makeSelectKeycloakToken } from 'containers/AppContainer/selectors';
import { requestSagisClaimInfo } from 'containers/CourtManagerLegalProceedings/Provision/redux/actions';
import { renderSearchQuery } from 'containers/ExpertAssignedMissionShow/utils';

export function* rejectClaim(action: any): Generator<unknown> {
  const {
    claimId,
    rejectionReasons,
    otherRejectionReason,
    decisionDetails,
    successCallback,
  } = action;
  const token = get(userToken.get(), 'token');
  if (!token) return;
  const path = `/corporeal/claim/reject/${claimId}`;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      rejectionReasons,
      otherRejectionReason,
      decisionDetails,
      isInboxesActive: featureService.hasFeature(
        FEATURES.ATTRIBUTION_INBOXES_MANAGER,
      ),
      isTrackingActive: featureService.hasFeature(
        FEATURES.TRACKING_ACTIONS_USER,
      ),
    }),
  };
  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield successCallback();
  }
}

export function* rejectClaimSaga() {
  yield takeLeading(REQUEST_REJECT_CLAIM, rejectClaim);
}

export function* requestClaimRecapS(action: any): Generator {
  const { claimId } = action;
  const token = get(userToken.get(), 'token')
    ? get(userToken.get(), 'token')
    : yield select(makeSelectKeycloakToken());

  if (!token) return;
  const path = get(userToken.get(), 'token')
    ? `/corporeal/claim/${claimId}`
    : `/corporeal/claim/${claimId}?isIntervenant=true`;

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
  };

  yield callApi(path, {}, options, [], setClaimRecap);
}

export function* requestClaimRecapSaga() {
  yield takeLeading(REQUEST_CLAIM_RECAP, requestClaimRecapS);
}

export function* openClaim(action: any): Generator<unknown> {
  const { claimDetails, claimId, successCallback } = action;
  const path = `/corporeal/open-claim/${claimId}`;

  const token = get(userToken.get(), 'token');

  if (!token) return;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      claimDetails,
    }),
  };

  const errorResponse = yield callApi(path, {}, options, []);

  if (!errorResponse) {
    yield successCallback();
    yield put(requestGetFeeNotesByClaimId(claimId));
    yield put(requestClaimRecap(claimId));
  }
}

export function* openClaimSaga() {
  yield takeLeading(REQUEST_OPEN_CLAIM, openClaim);
}
export function* openClaimMixed(action: any): Generator<unknown> {
  const { claimDetails, claimId, successCallback } = action;
  const path = `/corporeal/validate-mixed-claim/${claimId}`;

  const token = get(userToken.get(), 'token');

  if (!token) return;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      claimDetails,
    }),
  };

  const errorResponse = yield callApi(path, {}, options, []);

  if (!errorResponse) {
    yield successCallback();
    yield put(requestGetFeeNotesByClaimId(claimId));
    yield put(requestClaimRecap(claimId));
    yield put(requestSearchClaims(renderSearchQuery(claimDetails)));
  }
}

export function* openClaimMixedSaga() {
  yield takeLeading(REQUEST_OPEN_MIXED_CLAIM, openClaimMixed);
}
export function* assignTo(action: AssignAction): Generator<unknown> {
  const { payload, successCallback } = action;

  const openedClaim = yield select(makeSelecOpenedClaim());
  const path = payload.isDefaultAssignment
    ? `/corporeal/assignment/assign`
    : `/corporeal/assignment/assign/v2`;

  const token = get(userToken.get(), 'token');
  if (!token) return;
  const formattedPayload = formattedAssignementClaim(payload, openedClaim);
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...formattedPayload,
    }),
  };

  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield successCallback();
    if (payload.claimId) yield put(requestClaimRecap(payload.claimId));
  }
}

export function* assignToSaga() {
  yield takeLeading(REQUEST_ASSIGNMENT, assignTo);
}

export function* requestCorporealLawyers(): Generator<unknown> {
  const token = get(userToken.get(), 'token');
  const tokenLawyer = yield select(makeSelectKeycloakToken());

  const path = tokenLawyer
    ? `/corporeal/users?isIntervenant=true`
    : `/corporeal/users`;
  const options = {
    method: 'GET',
    headers: {
      authorization: `Bearer ${tokenLawyer ? tokenLawyer : token}`,
    },
  };
  if (!tokenLawyer && !token) {
    return null;
  }
  yield callApi(path, {}, options, [], setCorporealLawyers);
}

export function* requestCorporealLawyerSaga() {
  yield takeLeading(REQUEST_CORPOREAL_LAWYERS, requestCorporealLawyers);
}

export function* requestUpdateProvisionIntervenant(
  action: any,
): Generator<unknown> {
  const { payload, successCallback } = action;
  const token = get(userToken.get(), 'token');
  const path = `/corporeal/provision`;
  const options = {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...payload,
    }),
  };
  if (!token) {
    return null;
  }
  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    if (payload.claimNumber) {
      yield put(requestSagisClaimInfo(payload.claimNumber, successCallback));
    } else {
      yield successCallback();
    }
    yield put(requestClaimRecap(payload.claimId));
    yield put(
      requestSearchClaims({
        claimNumber: get(payload, 'claimNumber', null),
        withDocuments: 'Y',
      }),
    );
  }
}

export function* requestUpdateProvisionIntervenantSaga() {
  yield takeLeading(
    REQUEST_VALIDATE_PROVISION_INTERVENANT,
    requestUpdateProvisionIntervenant,
  );
}

export function* requestUpdateVictimInfo(action: any): Generator<unknown> {
  const { payload, successCallback } = action;
  const token = get(userToken.get(), 'token');
  const path = `/corporeal/change-victim`;

  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...payload,
    }),
  };
  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield successCallback();
    yield put(requestClaimRecap(payload.claimId));
  }
}

export function* requestUpdateVictimInfoSaga() {
  yield takeLeading(REQUEST_UPDATE_VICTIM_INFO, requestUpdateVictimInfo);
}
export function* requestUpdateBeneficiaryInfo(action: any): Generator<unknown> {
  const { payload, successCallback } = action;
  const token = get(userToken.get(), 'token');
  const path = `/corporeal/change-beneficiary`;

  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...payload,
    }),
  };
  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield successCallback();
    yield put(requestClaimRecap(payload.claimId));
  }
}

export function* requestUpdateBeneficiaryInfoSaga() {
  yield takeLeading(
    REQUEST_UPDATE_BENEFICIARY_INFO,
    requestUpdateBeneficiaryInfo,
  );
}
export function* requestUpdateVehicleInfo(action: any): Generator<unknown> {
  const { payload, successCallback } = action;
  const token = get(userToken.get(), 'token');
  const path = `/corporeal/change-vehicle`;

  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...payload,
    }),
  };
  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield successCallback();
    yield put(requestClaimRecap(payload.claimId));
  }
}
export function* requestUpdateVehicleInfoSaga() {
  yield takeLeading(REQUEST_UPDATE_VEHICLE_INFO, requestUpdateVehicleInfo);
}
export function* requestValidateVictim(action: any): Generator<unknown> {
  const { payload, successCallback } = action;
  const token = get(userToken.get(), 'token');
  const path = `/corporeal/validate-victim`;

  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...payload,
    }),
  };
  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield successCallback();
    yield put(requestClaimRecap(payload.id));
  }
}
export function* requestValidateVictimSaga() {
  yield takeLeading(REQUEST_VALIDATE_VICTIM, requestValidateVictim);
}
export function* requestValidateVehicle(action: any): Generator<unknown> {
  const { payload, successCallback } = action;
  const token = get(userToken.get(), 'token');
  const path = `/corporeal/validate-vehicle`;

  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...payload,
    }),
  };
  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield successCallback();
    yield put(requestClaimRecap(payload.id));
    yield put(requestSagisClaimInfo(payload.claimNumbers, () => {}));
    yield put(
      requestSearchClaims({
        claimNumber: payload.claimNumbers,
        withDocuments: 'Y',
      }),
    );
  }
}
export function* requestValidateVehicleSaga() {
  yield takeLeading(REQUEST_VALIDATE_VEHICLE, requestValidateVehicle);
}
export function* requestAddClaimNumber(action: any): Generator<unknown> {
  const { claimInfo } = action;
  const token = get(userToken.get(), 'token');
  const path = `/corporeal/claim`;

  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      id: claimInfo.id,
      claimInfo: { claimNumber: claimInfo.claimNumber },
    }),
  };

  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield put(requestClaimRecap(claimInfo.id));
  }
}

export function* requestAddClaimNumberSaga() {
  yield takeLeading(REQUEST_ADD_CLAIM_NUMBER, requestAddClaimNumber);
}
export function* requestFeeNotesByClaimId(action: {
  claimId: string;
  type: string;
}): Generator<unknown> {
  const token = get(userToken.get(), 'token');

  const path = `/corporeal/fee-note/GetByClaimId/manager/${
    action.claimId
  }?isOpeningManager=${true}`;
  const options = {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  yield callApi(path, {}, options, [], setFeeNotesSearch);
}
export function* requestGetFeeNotesByClaimIdSaga() {
  yield takeLeading(
    REQUEST_GET_FEE_NOTES_BY_CLAIM_ID,
    requestFeeNotesByClaimId,
  );
}
export function* getSagisClaim(action: any): Generator<unknown> {
  const claimNumber = action.claimNumber;
  const token = get(userToken.get(), 'token');
  const path = `/corporeal/claim-sagis/${claimNumber}`;
  if (isNull(token)) return;
  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  yield callApi(path, {}, options, [], setSagisClaim);
}
export function* requestGetSagisClaimInfoSaga() {
  yield takeLeading(REQUEST_GET_SAGIS_CLAIM, getSagisClaim);
}

export function* requestValidateCompletionClaimManager(
  action: any,
): Generator<unknown> {
  const { payload, successCallBack } = action;
  const token = get(userToken.get(), 'token');

  const path = `/corporeal/validate-completion-claim-manager`;

  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...payload,
    }),
  };
  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield successCallBack();
    yield put(requestClaimRecap(payload.claimId));
  }
}

export function* requestValidateCompletionClaimManagerSaga() {
  yield takeLeading(
    REQUEST_VALIDATE_COMPLETION_CLAIM_MANAGER,
    requestValidateCompletionClaimManager,
  );
}
export default [
  rejectClaimSaga,
  requestClaimRecapSaga,
  openClaimSaga,
  assignToSaga,
  requestCorporealLawyerSaga,
  openClaimMixedSaga,
  requestUpdateVictimInfoSaga,
  requestUpdateBeneficiaryInfoSaga,
  requestUpdateVehicleInfoSaga,
  requestValidateVictimSaga,
  requestValidateVehicleSaga,
  requestUpdateProvisionIntervenantSaga,
  requestAddClaimNumberSaga,
  requestGetFeeNotesByClaimIdSaga,
  requestGetSagisClaimInfoSaga,
  requestValidateCompletionClaimManagerSaga,
];
